import HistoryOutlined from '@ant-design/icons/lib/icons/HistoryOutlined';
import App from 'antd/lib/app';
import Button from 'antd/lib/button';
import Layout from 'antd/lib/layout';
import { Key } from 'antd/lib/table/interface';
import Tooltip from 'antd/lib/tooltip';
import Tag from 'antd/lib/tag';
import { TablePaginationConfig } from 'antd/lib/table';
import { FilterValue, SorterResult } from 'antd/lib/table/interface';
import React, { useContext, useReducer } from 'react';
import { AbilityContext } from 'src/components/ability/can';
import { ActionList, IAction } from 'src/components/table/actionList/actionList';
import { useDocumentTitle } from 'src/components/useDocumentTitle';
import { compareWithLocale } from '../../../domain/compareWithLocale';
import { ModalState, PjmLocationsModal, reducer } from './pjmLocationsReducer';
import { PageHeader } from 'src/components/PageHeader';
import { PlusCircleOutlined, UploadOutlined } from '@ant-design/icons';
import { WithEmptyDataTable } from 'src/components/table/withEmptyDataTable';
import { AuditTrailModal } from 'src/components/modals/AuditTrailModal/AuditTrailModal';
import { AuditEntityType } from 'src/domain/audit-trail/interface';
import { PjmBreadcrumbNav } from '../PjmBreadcrumbNav';
import { usePjmLocationsListQuery } from 'src/domain/pjm/queries';
import { DrhubSyncStatus } from '../DrhubSyncStatus';
import { StatusTag } from 'src/components/StatusTag';
import { PjmLocationId } from './components/PjmLocationId';
import { usePageLocation } from '../../usePageState';
import { useLocation } from 'react-router-dom';
import { DEFAULT_PAGINATION } from 'src/domain/commonConst';
import { toUsefulParams } from '../../toUsefulParams';
import { getTablePaginationParams } from 'src/components/table/getTablePaginationParams';
import { SearchInput } from 'src/components/searchInput/searchInput';
import { PjmLocationsImportModal } from './components/PjmLocationsImportModal';
import { PjmLocationDto } from 'src/domain/pjm/dto/PjmLocationDto';
import { PjmLocationsExportButton } from './components/PjmLocationsExportButton';
import { PjmLocationModal } from './components/PjmLocationModal/PjmLocationModal';
import { PjmLocationSidebar } from './components/PjmLocationSidebar/PjmLocationSidebar';
import { usePjmLocationsSidebar } from './components/PjmLocationSidebar/usePjmLocationsSidebar';
import { CustomizableTableContextProvider } from '../../../components/table/CustomizableTableContext';
import { CustomizableColumnType, CustomizableTable } from '../../../components/table/CustomizableTable';
import { PjmLocationsBulkUpdateModal } from './components/PjmLocationsBulkUpdateModal';
import { useTableSelection } from 'src/components/table/useTableSelection';
import { SidebarGrid } from '../../../components/Sidebar/SidebarGrid';
import { PjmLoadReductionType } from '../../../domain/pjm/dto/enums';
import { PjmLocationReductionInfo } from '../../../domain/pjm/dto/PjmLocationReductionInfo';

import './PjmLocations.css';

export const PjmLocations = () => {
    const { notification } = App.useApp();

    useDocumentTitle('PJM Locations');

    const { isFiltersSidebarEnabled, sidebarCollapsed, setSidebarCollapsed, layoutPaddingLeft } = usePjmLocationsSidebar();
    const ability = useContext(AbilityContext);
    const location = useLocation();

    const initialState: ModalState = { modal: undefined, target: undefined };
    const [modalState, dispatch] = useReducer(reducer, initialState);

    const { setPageQuery, queryToState } = usePageLocation({ parseNumbers: false });
    const pageState: any = queryToState(location.search);

    const { selectedKeys, selectedItems, handleSelectedItems } = useTableSelection('uuid');

    const listQuery = {
        pagination: pageState?.pagination ?? DEFAULT_PAGINATION,
        ...(pageState?.filter && { filter: pageState?.filter }),
        ...(pageState?.sorter && { sorter: pageState?.sorter }),
        ...(pageState?.search && { search: pageState?.search }),
        include: 'registration',
    };

    const { data: locations, isError, error, isFetching, refetch } = usePjmLocationsListQuery(listQuery);
    if (isError) {
        notification.error({ key: 'fetch-pjm-locations-error', message: error.message || 'Cannot fetch PJM Locations!' });
    }

    function onModalClose(actionTaken: boolean) {
        dispatch({ type: 'CLOSE' });
        if (actionTaken) {
            handleSelectedItems([], []);
            refetch();
        }
    }

    async function onTableChange(
        pagination: TablePaginationConfig,
        filter: Record<string, FilterValue | null>,
        sorter: SorterResult<PjmLocationDto> | SorterResult<PjmLocationDto>[]
    ) {
        const newFilter = {
            ...pageState.filter,
            ...filter,
        };

        const preparedParams = toUsefulParams({ pagination, sorter, filter: newFilter });

        setPageQuery({ ...pageState, ...preparedParams });
    }

    const onSelectRow = (selectedRows: Key[]) => {
        handleSelectedItems(selectedRows as number[], locations?.data as any[]);
    };

    const onSearch = async (value: string) => {
        setPageQuery({ ...pageState, search: value });
    };

    function makeYesOrNoTag(value: boolean | undefined) {
        switch (value) {
            case true:
                return <Tag color="success">Yes</Tag>;
            case false:
                return <Tag color="error">No</Tag>;
            default:
                return '';
        }
    }

    function getReductionInfoByType(reductionInfoList: PjmLocationReductionInfo[], reductionType: PjmLoadReductionType) {
        const reductionInfo = reductionInfoList?.find(item => item.reductionType === reductionType);

        return reductionInfo?.reductionValue;
    }

    const actions: IAction[] = [
        {
            title: 'Edit',
            onClick: (record: PjmLocationDto) => () => dispatch({ type: 'EDIT', payload: record }),
        },
        {
            title: 'Audit Trail',
            icon: <HistoryOutlined />,
            onClick: (record: PjmLocationDto) => () => dispatch({ type: 'AUDIT', payload: record }),
        },
    ];

    const columns: CustomizableColumnType<PjmLocationDto>[] = [
        {
            key: 'location_name',
            title: 'Location',
            dataIndex: ['summaryInfo', 'name'],
            minWidth: 200,
            fixed: 'left',
            sorter: (a, b) => compareWithLocale(a.summaryInfo.name, b.summaryInfo.name),
            render: (text, record) => <>{text}</>,
        },
        {
            key: 'edc_account_number',
            title: 'EDC Account Number',
            dataIndex: ['summaryInfo', 'EDC', 'accountNo'],
            minWidth: 100,
            sorter: (a, b) => compareWithLocale(a.summaryInfo.EDC.accountNo, b.summaryInfo.EDC.accountNo),
        },
        {
            key: 'location_id',
            title: 'Location ID',
            dataIndex: 'id',
            minWidth: 100,
            sorter: (a, b) => compareWithLocale(a.id, b.id),
            render: (text, record) => <PjmLocationId value={text} />,
        },
        {
            key: 'status',
            title: 'Status',
            dataIndex: ['summaryInfo', 'status'],
            minWidth: 120,
            sorter: (a, b) => compareWithLocale(a.summaryInfo.status, b.summaryInfo.status),
            render: (text, record) => <StatusTag status={text} />,
        },
        {
            key: 'zone',
            title: 'Zone',
            dataIndex: ['summaryInfo', 'zone'],
            minWidth: 100,
            sorter: (a, b) => compareWithLocale(a.summaryInfo.zone, b.summaryInfo.zone),
        },
        {
            key: 'pricing_point',
            title: 'Pricing Point',
            dataIndex: ['summaryInfo', 'pricingPoint'],
            minWidth: 220,
            hidden: true, // Hide by default
        },
        {
            key: 'business_segment',
            title: 'Business Segment',
            dataIndex: ['siteInfo', 'businessSegment'],
            minWidth: 200,
            hidden: true, // Hide by default
        },
        {
            key: 'address1',
            title: 'Address1',
            dataIndex: ['siteInfo', 'address', 'address1'],
            minWidth: 120,
            hidden: true, // Hide by default
        },
        {
            key: 'address2',
            title: 'Address2',
            dataIndex: ['siteInfo', 'address', 'address2'],
            minWidth: 120,
            hidden: true, // Hide by default
        },
        {
            key: 'city',
            title: 'City',
            dataIndex: ['siteInfo', 'address', 'city'],
            minWidth: 120,
            hidden: true, // Hide by default
        },
        {
            key: 'state',
            title: 'State',
            dataIndex: ['siteInfo', 'address', 'state'],
            minWidth: 100,
        },
        {
            key: 'zip_code',
            title: 'Zip Code',
            dataIndex: ['siteInfo', 'address', 'zipCode'],
            minWidth: 100,
        },
        {
            key: 'edc_name',
            title: 'EDC',
            dataIndex: ['summaryInfo', 'EDC', 'name'],
            minWidth: 60,
            sorter: (a, b) => compareWithLocale(a.summaryInfo.EDC.name, b.summaryInfo.EDC.name),
        },
        {
            key: 'csp_name',
            title: 'CSP',
            dataIndex: ['summaryInfo', 'CSP', 'name'],
            minWidth: 60,
            hidden: true, // Hide by default
            sorter: (a, b) => compareWithLocale(a.summaryInfo.CSP.name, b.summaryInfo.CSP.name),
        },
        {
            key: 'csp_account_number',
            title: 'CSP Account Number',
            dataIndex: ['summaryInfo', 'CSP', 'accountNo'],
            minWidth: 60,
            hidden: true, // Hide by default
        },
        {
            key: 'registrations_count',
            title: 'Registrations',
            dataIndex: ['registrations'],
            minWidth: 60,
            render: (text, record) => record.registrations?.length || 0,
        },
        {
            key: 'sync_drhub_status',
            title: 'DRHUB Sync',
            dataIndex: 'syncDrhubStatus',
            minWidth: 120,
            render: (text, record) => <DrhubSyncStatus value={record} />,
        },
        {
            key: 'allow_edc_to_submit_data',
            title: 'Allow EDC to Submit Data',
            dataIndex: ['summaryInfo', 'allowEDCtoSubmitData'],
            minWidth: 60,
            hidden: true, // Hide by default
            render: (text, record) => makeYesOrNoTag(record.summaryInfo?.allowEDCtoSubmitData),
        },
        {
            key: 'allow_lse_to_submit_data',
            title: 'Allow LSE to Submit Data',
            dataIndex: ['summaryInfo', 'allowLSEtoSubmitData'],
            minWidth: 60,
            hidden: true, // Hide by default
            render: (text, record) => makeYesOrNoTag(record.summaryInfo?.allowLSEtoSubmitData),
        },
        {
            key: 'utility_residential_participant',
            title: 'Utility Residential Participation?',
            dataIndex: ['siteInfo', 'utilityResidentialParticipant'],
            minWidth: 60,
            hidden: true, // Hide by default
            render: (text, record) => makeYesOrNoTag(record.siteInfo?.utilityResidentialParticipant),
        },
        {
            key: 'peak_load_contribution',
            title: 'Peak Load Contribution (kW)',
            dataIndex: ['profileInfo', 'peakLoadContribution'],
            minWidth: 150,
            hidden: true, // Hide by default
        },
        {
            key: 'winter_peak_load',
            title: 'Winter Peak Load (kW)',
            dataIndex: ['profileInfo', 'winterPeakLoad'],
            minWidth: 150,
            hidden: true, // Hide by default
        },
        {
            key: 'capacity_loss_factor',
            title: 'Capacity Loss Factor (kW)',
            dataIndex: ['profileInfo', 'capacityLossFactor'],
            minWidth: 150,
            hidden: true, // Hide by default
        },
        {
            key: 'energy_loss_factor',
            title: 'Energy Loss Factor (kW)',
            dataIndex: ['profileInfo', 'energyLossFactor'],
            minWidth: 150,
            hidden: true, // Hide by default
        },
        {
            key: 'max_load',
            title: 'Max Load (kW)',
            dataIndex: ['profileInfo', 'maxLoad'],
            minWidth: 150,
            hidden: true, // Hide by default
        },
        {
            key: 'loadReduction',
            title: 'Maximum Load Reduction Capability (kW)',
            dataIndex: ['profileInfo', 'loadReduction'],
            minWidth: 150,
            hidden: true, // Hide by default
        },
        {
            key: 'manufacturing',
            title: 'Manufacturing (kW)',
            dataIndex: ['reductionInfo', 'MANUFACTURING'],
            minWidth: 150,
            hidden: true, // Hide by default
            render: (text, record) => getReductionInfoByType(record?.reductionInfo || [], PjmLoadReductionType.MANUFACTURING),
        },
        {
            key: 'hvac',
            title: 'HVAC (kW)',
            dataIndex: ['reductionInfo', 'HVAC'],
            minWidth: 150,
            hidden: true, // Hide by default
            render: (text, record) => getReductionInfoByType(record?.reductionInfo || [], PjmLoadReductionType.HVAC),
        },
        {
            key: 'generator',
            title: 'Generator (kW)',
            dataIndex: ['reductionInfo', 'GENERATOR'],
            minWidth: 150,
            hidden: true, // Hide by default
            render: (text, record) => getReductionInfoByType(record?.reductionInfo || [], PjmLoadReductionType.GENERATOR),
        },
        {
            key: 'generator_count',
            title: 'Number of Generators',
            dataIndex: ['generatorCount'],
            minWidth: 150,
            hidden: true, // Hide by default
        },
        {
            key: 'order202c',
            title: '202c Specified Resource?',
            minWidth: 150,
            hidden: true, // Hide by default
            render: (text, record) => (record.generatorInfo?.some(generator => generator.order202c) ? 'Yes' : 'No'),
        },
        {
            key: 'batteries',
            title: 'Batteries (kW)',
            dataIndex: ['reductionInfo', 'BATTERIES'],
            minWidth: 150,
            hidden: true, // Hide by default
            render: (text, record) => getReductionInfoByType(record?.reductionInfo || [], PjmLoadReductionType.BATTERIES),
        },
        {
            key: 'lighting',
            title: 'Lighting (kW)',
            dataIndex: ['reductionInfo', 'LIGHTING'],
            minWidth: 150,
            hidden: true, // Hide by default
            render: (text, record) => getReductionInfoByType(record?.reductionInfo || [], PjmLoadReductionType.LIGHTING),
        },
        {
            key: 'water_heaters',
            title: 'Water Heaters (kW)',
            dataIndex: ['reductionInfo', 'WATER_HEATERS'],
            minWidth: 150,
            hidden: true, // Hide by default
            render: (text, record) => getReductionInfoByType(record?.reductionInfo || [], PjmLoadReductionType.WATER_HEATERS),
        },
        {
            key: 'refrigeration',
            title: 'Refrigeration (kW)',
            dataIndex: ['reductionInfo', 'REFRIGERATION'],
            minWidth: 150,
            hidden: true, // Hide by default
            render: (text, record) => getReductionInfoByType(record?.reductionInfo || [], PjmLoadReductionType.REFRIGERATION),
        },
        {
            key: 'plug_load',
            title: 'Plug Load (kW)',
            dataIndex: ['reductionInfo', 'PLUG_LOAD'],
            minWidth: 150,
            hidden: true, // Hide by default
            render: (text, record) => getReductionInfoByType(record?.reductionInfo || [], PjmLoadReductionType.PLUG_LOAD),
        },
        {
            key: 'computing',
            title: 'Computing (kW)',
            dataIndex: ['reductionInfo', 'COMPUTING'],
            minWidth: 150,
            hidden: true, // Hide by default
            render: (text, record) => getReductionInfoByType(record?.reductionInfo || [], PjmLoadReductionType.COMPUTING),
        },

        ...(ability.can('update', 'PjmLocation')
            ? ([
                  {
                      key: 'action',
                      fixed: 'right',
                      minWidth: 60,
                      render: (text, record) => <ActionList actions={actions} item={record} />,
                  },
              ] as CustomizableColumnType<PjmLocationDto>[])
            : []),
    ];

    const pageHeaderActions: React.ReactNode[] = [];
    if (selectedKeys.length > 0) {
        pageHeaderActions.push(
            <Button size="large" key="clear-selection-action" onClick={() => handleSelectedItems([], [])}>
                Clear Selected ({selectedKeys.length})
            </Button>
        );
    }

    pageHeaderActions.push([
        <SearchInput onSearch={onSearch} defaultValue={pageState?.search} key="pjm-locations-search" />,
        <PjmLocationsExportButton filter={{ search: pageState?.search, filter: pageState?.filter }} key="pjm-locations-export" />,
    ]);

    if (ability.can('update', 'PjmLocation')) {
        pageHeaderActions.push(
            <Tooltip title="You may select one or more locations and click this button so we prepare a bulk update template file containing the selected locations.">
                <Button size="large" key="pjm-locations-bulk-update" onClick={() => dispatch({ type: 'BULK_UPDATE' })} icon={<UploadOutlined />}>
                    Bulk Update
                </Button>
            </Tooltip>
        );
    }

    if (ability.can('create', 'PjmLocation')) {
        pageHeaderActions.push(
            <Tooltip title="Here, you can import a file containing locations (and their registrations) to create them in bulk.">
                <Button size="large" key="pjm-locations-import" onClick={() => dispatch({ type: 'IMPORT' })} icon={<UploadOutlined />}>
                    Import
                </Button>
            </Tooltip>
        );
        pageHeaderActions.push(
            <Button size="large" key="pjm-locations-add" type="primary" onClick={() => dispatch({ type: 'CREATE' })} icon={<PlusCircleOutlined />}>
                Add Location
            </Button>
        );
    }

    return (
        <>
            <CustomizableTableContextProvider configKey="pjmLocations" columns={columns}>
                <Layout style={{ height: '100%', paddingLeft: layoutPaddingLeft }}>
                    {isFiltersSidebarEnabled && (
                        <PjmLocationSidebar
                            collapsed={sidebarCollapsed}
                            onCollapseChange={(collapsed: boolean) => setSidebarCollapsed(collapsed)}
                            totalRecords={getTablePaginationParams(locations?.meta).total}
                            onFilterChange={() => {}}
                            gridComponent={<SidebarGrid />}
                        />
                    )}

                    <Layout.Content style={{ padding: '0 26px' }}>
                        <PageHeader pageTitle={<PjmBreadcrumbNav />} actions={pageHeaderActions} />
                        <WithEmptyDataTable condition={isFetching}>
                            <CustomizableTable<PjmLocationDto>
                                showHiddenColumnsCount
                                size="small"
                                rowKey="uuid"
                                className="pjm-locations-table"
                                sticky={{ offsetHeader: 62 }}
                                dataSource={locations?.data || []}
                                loading={isFetching}
                                onChange={onTableChange}
                                meta={locations?.meta!}
                                rowSelection={
                                    ability.can('update', 'PjmLocation')
                                        ? {
                                              selectedRowKeys: selectedKeys,
                                              preserveSelectedRowKeys: true,
                                              onChange: onSelectRow,
                                          }
                                        : undefined
                                }
                            />
                        </WithEmptyDataTable>
                    </Layout.Content>
                </Layout>
            </CustomizableTableContextProvider>

            {modalState.modal === PjmLocationsModal.EDIT && (
                <PjmLocationModal key="pjm-locations-modal" onClose={onModalClose} value={modalState.target} />
            )}
            {modalState.modal === PjmLocationsModal.CREATE && <PjmLocationModal key="pjm-locations-modal" onClose={onModalClose} />}
            {modalState.modal === PjmLocationsModal.IMPORT && <PjmLocationsImportModal key="pjm-locations-import-modal" onClose={onModalClose} />}
            {modalState.modal === PjmLocationsModal.BULK_UPDATE && (
                <PjmLocationsBulkUpdateModal key="pjm-locations-bulk-update-modal" selectedItems={selectedItems} onClose={onModalClose} />
            )}
            {modalState.modal === PjmLocationsModal.AUDIT && (
                <AuditTrailModal
                    key="pjm-audit-trail-modal"
                    onClose={() => dispatch({ type: 'CLOSE' })}
                    entityId={modalState.target?.uuid!}
                    entityType={AuditEntityType.PJM_LOCATION}
                    pageSize={100}
                />
            )}
        </>
    );
};
